import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Users, UserCheck, Send, X, UserMinus, ArrowRight, Calendar, ClipboardList } from 'lucide-react';
import { useContext } from 'react';
import AuthContext from '../context/AuthContext';

function formatTelefone(tel) {
  if (!tel) return '—';
  const d = tel.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0,2)}) ${d[2]} ${d.slice(3,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return tel;
}
function formatData(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}
function formatSexo(s) {
  if (s === 'M') return 'Masculino';
  if (s === 'F') return 'Feminino';
  return s || '—';
}
function getDataHoje() {
  return new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}


export default function ClinicaDashboardPage() {
  const { user } = useContext(AuthContext);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
  const [evolucoes, setEvolucoes] = useState([]);
  const [loadingEvolucoes, setLoadingEvolucoes] = useState(false);
  const [abaModal, setAbaModal] = useState('dados');
  const [dataHoje] = useState(() => {
    const s = getDataHoje();
    return s.charAt(0).toUpperCase() + s.slice(1);
  });

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    api.get('/clinica/dashboard')
      .then(({ data }) => setDashboard(data.data))
      .catch(() => toast.error('Erro ao carregar dashboard'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleBuscar = async () => {
    if (!busca.trim()) return;
    setBuscando(true);
    try {
      const { data } = await api.get(`/clinica/buscar-fisios?q=${busca}`);
      setResultados(data.data);
    } catch { toast.error('Erro na busca'); }
    finally { setBuscando(false); }
  };

  const handleConvidar = async (fisioId) => {
    try {
      await api.post('/clinica/convites', { fisioterapeutaId: fisioId });
      toast.success('Convite enviado!');
      setResultados(r => r.filter(f => f._id !== fisioId));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao enviar convite');
    }
  };

  const handleDesvincular = async (fisioId, nome) => {
    if (!window.confirm(`Desvincular ${nome} da clínica?`)) return;
    try {
      await api.delete(`/clinica/equipe/${fisioId}`);
      toast.success('Fisioterapeuta desvinculado.');
      fetchDashboard();
    } catch { toast.error('Erro ao desvincular'); }
  };

  const abrirModal = (p) => {
    setPacienteSelecionado(p);
    setAbaModal('dados');
    setEvolucoes([]);
  };

  const carregarEvolucoes = async (patientId) => {
    setAbaModal('evolucoes');
    if (evolucoes.length > 0) return;
    setLoadingEvolucoes(true);
    try {
      const { data } = await api.get(`/clinica/evolucoes/${patientId}`);
      setEvolucoes(data.data);
    } catch { toast.error('Erro ao carregar evoluções'); }
    finally { setLoadingEvolucoes(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const nomeClinica = user?.nome || dashboard?.equipe?.[0]?.clinicaNome || 'Clínica';
  const totalFisios = dashboard?.totalFisioterapeutas ?? 0;
  const totalCompartilhados = dashboard?.totalPacientesCompartilhados ?? 0;
  const equipe = dashboard?.equipe ?? [];
  const pacientesCompartilhados = dashboard?.pacientesCompartilhados ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Olá, Clínica {nomeClinica} 👋
            </h1>
            <p className="text-gray-400 mt-1 text-sm">{dataHoje}</p>
          </div>

        </div>

        {/* Cards de resumo — 3 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { icon: <Users className="w-6 h-6 text-blue-500" />, bg: 'bg-blue-50', value: totalFisios, label: 'Fisioterapeutas vinculados' },
            { icon: <UserCheck className="w-6 h-6 text-purple-500" />, bg: 'bg-purple-50', value: totalCompartilhados, label: 'Pacientes compartilhados' },

          ].map((card, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${card.bg}`}>
                {card.icon}
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                <p className="text-xs text-gray-500">{card.label}</p>

              </div>
            </div>
          ))}
        </div>

        {/* Linha: Convidar + Equipe */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Convidar fisio */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Convidar Fisioterapeuta</h2>
            <p className="text-xs text-gray-400 mb-4">Busque por nome ou CREFITO para convidar um fisioterapeuta para sua clínica.</p>
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <input
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleBuscar()}
                  placeholder="Buscar por nome ou CREFITO..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" /></svg>
              </div>
              <button
                onClick={handleBuscar}
                disabled={buscando}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Buscar
              </button>
            </div>

            {resultados.length > 0 && (
              <ul className="mt-3 divide-y divide-gray-50">
                {resultados.map(f => (
                  <li key={f._id} className="flex items-center justify-between py-2.5">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{f.nome}</p>
                      <p className="text-xs text-gray-400">CREFITO: {f.crefito}</p>
                    </div>
                    <button onClick={() => handleConvidar(f._id)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                      <Send className="w-3 h-3" /> Convidar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Equipe */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Equipe Vinculada</h2>
            <p className="text-xs text-gray-400 mb-4">Fisioterapeutas que fazem parte da sua clínica.</p>
            {!equipe.length ? (
              <p className="text-sm text-gray-400 text-center py-6">Nenhum fisioterapeuta vinculado ainda.</p>
            ) : (
              <ul className="space-y-2">
                {equipe.map(f => (
                  <li key={f._id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 border border-gray-50">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{f.nome}</p>
                      <p className="text-xs text-gray-400 truncate">{f.email}</p>
                      <p className="text-xs text-gray-400">CREFITO: {f.crefito}</p>
                    </div>
                    <button
                      onClick={() => handleDesvincular(f._id, f.nome)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-red-500 border border-red-100 hover:bg-red-50 rounded-lg text-xs font-medium flex-shrink-0"
                    >
                      <UserMinus className="w-3 h-3" /> Desvincular
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {/* Placeholder convidar */}
            <div className="mt-3 flex items-center gap-3 p-3 rounded-xl border border-dashed border-gray-200">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">Convide novos fisioterapeutas</p>
                <p className="text-xs text-gray-400">Expanda sua equipe e ofereça um atendimento ainda melhor.</p>
              </div>
              <button
                onClick={() => document.querySelector('input[placeholder]')?.focus()}
                className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50"
              >
                Convidar
              </button>
            </div>
          </div>
        </div>

        {/* Linha: Pacientes compartilhados + Próximos atendimentos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Pacientes compartilhados */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Pacientes Compartilhados</h2>
            <p className="text-xs text-gray-400 mb-4">Pacientes que foram compartilhados com você.</p>
            {!pacientesCompartilhados.length ? (
              <p className="text-sm text-gray-400 text-center py-6">Nenhum paciente compartilhado.</p>
            ) : (
              <ul className="space-y-3">
                {pacientesCompartilhados.map(p => (
                  <li key={p._id} className="border border-gray-100 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <UserCheck className="w-5 h-5 text-purple-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{p.nome}</p>
                        <p className="text-xs text-gray-400">Fisio: {p.user?.nome}</p>
                        <p className="text-xs text-gray-400">Condição: {p.diagnosisTitle}</p>
                      </div>
                    </div>
                    <div className="flex gap-6 text-xs text-gray-500 mb-3 px-1">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <div>
                          <p className="text-gray-400">Última sessão</p>
                          <p className="font-medium text-gray-600">{p.ultimaSessao ? formatData(p.ultimaSessao) : '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-gray-400" />
                        <div>
                          <p className="text-gray-400">Sessões realizadas</p>
                          <p className="font-medium text-gray-600">{p.totalSessoesRealizadas ?? 0} / {p.numberOfSessions ?? '—'}</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => abrirModal(p)}
                      className="w-full flex items-center justify-center gap-1.5 py-2 border border-gray-200 text-gray-600 text-xs font-medium rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <ArrowRight className="w-3.5 h-3.5" /> Ver evolução
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>


        </div>

        <div className="text-center text-xs text-gray-400 pb-4">
          © {new Date().getFullYear()} <span className="text-blue-500 font-medium">FisioWeb</span>. Todos os direitos reservados.
        </div>
      </div>

      {/* Modal paciente */}
      {pacienteSelecionado && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md relative flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">{pacienteSelecionado.nome}</h3>
              <button onClick={() => setPacienteSelecionado(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex border-b border-gray-100">
              <button onClick={() => setAbaModal('dados')} className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${abaModal === 'dados' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>Dados</button>
              <button onClick={() => carregarEvolucoes(pacienteSelecionado._id)} className={`flex-1 py-2.5 text-sm font-medium border-b-2 flex items-center justify-center gap-1.5 transition-colors ${abaModal === 'evolucoes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>
                <ClipboardList className="w-4 h-4" /> Evoluções
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {abaModal === 'dados' && (
                <dl className="space-y-3 text-sm">
                  {[
                    ['Fisioterapeuta', pacienteSelecionado.user?.nome],
                    ['Diagnóstico', pacienteSelecionado.diagnosisTitle],
                    ['Sexo', formatSexo(pacienteSelecionado.sex)],
                    ['Nascimento', formatData(pacienteSelecionado.dateOfBirth)],
                    ['Telefone', formatTelefone(pacienteSelecionado.telefone)],
                    ['Sessões', pacienteSelecionado.numberOfSessions],
                  ].map(([label, value]) => (
                    <div key={label} className="flex gap-2">
                      <dt className="text-gray-500 w-32 shrink-0">{label}:</dt>
                      <dd className="text-gray-800">{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {abaModal === 'evolucoes' && (
                loadingEvolucoes ? <p className="text-sm text-gray-400 text-center py-8">Carregando...</p>
                : !evolucoes.length ? <p className="text-sm text-gray-400 text-center py-8">Nenhuma evolução registrada.</p>
                : <ul className="space-y-3">
                    {evolucoes.map(ev => (
                      <li key={ev._id} className="border border-gray-100 rounded-xl p-4">
                        <p className="text-xs text-gray-400 mb-1">{ev.session?.date ? formatData(ev.session.date) : '—'}</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{ev.description}</p>
                      </li>
                    ))}
                  </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}